import { useCallback, useState } from 'react';
import {
  ContractType,
  GetContractsV2QueryVariables,
  useGetContractsV2LazyQuery,
} from '../types';

// size is capped at 100 on the BE
export interface UseFetchAllContractsResult {
  fetchAllContracts: (
    variables: Omit<GetContractsV2QueryVariables, 'page'>,
  ) => Promise<ContractType[]>;
  loading: boolean;
  error: boolean;
}

export const useFetchAllContracts = (): UseFetchAllContractsResult => {
  const [getContracts] = useGetContractsV2LazyQuery();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchAllContracts = useCallback(
    async (
      variables: Omit<GetContractsV2QueryVariables, 'page'>,
    ): Promise<ContractType[]> => {
      setLoading(true);
      setError(false);
      try {
        const size = variables.size ?? 100;
        const firstPage = await getContracts({
          variables: { ...variables, page: 1 },
        });

        const allItems = [...(firstPage.data?.getContractsV2?.items ?? [])];
        const total = firstPage.data?.getContractsV2?.total ?? 0;
        const totalPages = Math.ceil(total / size);

        const remainingPages = Array.from(
          { length: totalPages - 1 },
          (_, i) => i + 2,
        );
        const results = await Promise.all(
          remainingPages.map(page =>
            getContracts({ variables: { ...variables, page } }),
          ),
        );
        for (const result of results) {
          allItems.push(...(result.data?.getContractsV2?.items ?? []));
        }

        return allItems;
      } catch {
        setError(true);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [getContracts],
  );

  return { fetchAllContracts, loading, error };
};
